import { isColumn } from '@lumapps/wrex-columns/utils/isColumn';
import { NodeEntry, ReactEditor } from '@lumapps/wrex/slate';
import { findParent } from '@lumapps/wrex/slate/utils/findParent';
import { normalizeLiftToRootNode } from '@lumapps/wrex/slate/utils/normalizeLiftToRootNode';

import { IMAGE_FEATURES } from '../../constants';
import { ImageEditor, ImageEditorOptions, type ImageGroupElement } from '../../types';
import { isAlignedImageWrapper } from '../../utils/isAlignedImageWrapper';
import { isImageGroup } from '../../utils/isImageGroup';
import { uploadExternalImage } from '../../utils/uploadExternalImage';

export function normalizeImage(
    options: ImageEditorOptions,
    editor: ReactEditor & ImageEditor,
    [node, path]: NodeEntry,
) {
    if (!isImageGroup(node)) {
        return false;
    }

    if (node.external) {
        uploadExternalImage(options, editor, [node, path]);
    }
    const parentAlignmentWrapper = findParent(editor, path, isAlignedImageWrapper);

    const columnParent = findParent(editor, path, isColumn);
    // check if it's nested but not in parentAlignmentWrapper
    let shouldLiftToRoot = !parentAlignmentWrapper && path.length > 1;

    // when in a column we don't want images to be lifted to root
    // (we ignore alignment wrapper since not used in columns)
    if (columnParent) {
        shouldLiftToRoot = false;
    }

    if (shouldLiftToRoot) {
        normalizeLiftToRootNode(editor, [node, path], isImageGroup);
    }

    // If link option is disabled, remove the link from the image
    if (!options.enabledFeatures?.includes(IMAGE_FEATURES.link)) {
        const hasLinks = node.images?.some((image) => !!image.link);
        if (hasLinks) {
            editor.setNodes<ImageGroupElement>(
                {
                    images: node.images.map((image) =>
                        // Completely remove the link property from the image object
                        ({ ...image, link: undefined }),
                    ),
                },
                {
                    at: path,
                },
            );
        }
    }

    return false;
}
